import React, { useState } from "react";
import { FiDownload, FiTrash2, FiLoader, FiMic, FiUser, FiInfo } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { UploadCard, useJobHistory, formatSeconds } from "./audioShared";

const QUALITIES = [
  { key: "fast", label: "Fast", hint: "Quickest, a little less polished." },
  { key: "balanced", label: "Balanced", hint: "Good quality in reasonable time." },
  { key: "best", label: "Best", hint: "Highest quality, takes the longest." },
];

// AI Voice Cloning: an original recording + a sample of the user's own voice -> the original, spoken in
// that voice. The work runs on the server in the background; this page polls for the result.
export default function VoiceCloningTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("clone");
  const [original, setOriginal] = useState(null);
  const [reference, setReference] = useState(null);
  const [quality, setQuality] = useState("balanced");
  const [speed, setSpeed] = useState(1);
  const [singing, setSinging] = useState(false);
  const [pitchShift, setPitchShift] = useState(0);
  const [consent, setConsent] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const canSubmit = original && reference && consent && !submitting && !running;

  const submit = async () => {
    setError("");
    setFieldErrors({});
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("original", original);
    form.append("reference", reference);
    form.append("quality", quality);
    form.append("speed", String(speed));
    form.append("singing", String(singing));
    form.append("pitch_shift", String(pitchShift));
    form.append("consent", String(consent));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.original || data?.reference) setFieldErrors({ original: data.original, reference: data.reference });
      return setError(data?.original || data?.reference ? "" : errorMessage(data));
    }
    add(data);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Voice Cloning</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Upload a recording, then a sample of your own voice. We keep the words, timing and expression of the original
        and change the voice, so it sounds like you are speaking.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <UploadCard
          step={1} kind="original" icon={FiMic} title="Original audio"
          help="The recording to convert - the speech you want to hear in your voice."
          picked={original} onPick={setOriginal} onClear={() => setOriginal(null)} problem={fieldErrors.original}
        />
        <UploadCard
          step={2} kind="reference" icon={FiUser} title="Your voice"
          help="A clear sample of the voice to copy: 10 to 20 seconds of normal speech, no music or noise."
          picked={reference} onPick={setReference} onClear={() => setReference(null)} problem={fieldErrors.reference}
        />
      </div>

      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 mb-6">
        <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4 block">Options</label>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <span className="text-sm text-gray-300 block mb-2">Quality</span>
            <div className="flex flex-wrap gap-2">
              {QUALITIES.map((q) => (
                <button key={q.key} type="button" title={q.hint} onClick={() => setQuality(q.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                    quality === q.key ? "bg-accent-light text-navy" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"
                  }`}>
                  {q.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">{QUALITIES.find((q) => q.key === quality)?.hint}</p>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-300">Speaking speed</span>
              <span className="text-accent-light font-medium">{speed.toFixed(2)}&times;</span>
            </div>
            <input type="range" min="0.7" max="1.4" step="0.05" value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="w-full accent-accent-light" />
            <div className="flex justify-between text-[10px] text-gray-500"><span>Slower</span><span>Faster</span></div>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-start gap-2.5 text-sm text-gray-300 cursor-pointer select-none">
              <input type="checkbox" checked={singing} onChange={(e) => setSinging(e.target.checked)} className="mt-0.5 accent-accent-light" />
              <span>
                The original is singing
                <span className="block text-xs text-gray-500">Keeps the melody of the original. Leave off for normal speech.</span>
              </span>
            </label>
            {singing && (
              <div className="mt-4 max-w-md">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">Pitch shift</span>
                  <span className="text-accent-light font-medium">{pitchShift > 0 ? "+" : ""}{pitchShift} semitones</span>
                </div>
                <input type="range" min="-12" max="12" step="1" value={pitchShift} onChange={(e) => setPitchShift(Number(e.target.value))} className="w-full accent-accent-light" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-5 mb-6 text-sm text-amber-100">
        <label className="flex items-start gap-2.5 cursor-pointer select-none">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-amber-400" />
          <span>
            I confirm that the voice sample is my own voice, or that I have the permission of the person it belongs to.
            <span className="block text-xs text-amber-200/70 mt-1">Do not use this tool to imitate someone without their consent or to mislead people.</span>
          </span>
        </label>
      </div>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mb-6">{error}</p>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Uploading...</> : running ? <><FiLoader className="animate-spin" /> Converting...</> : <><FiMic /> Convert to my voice</>}
      </button>
      {!canSubmit && !running && !submitting && (
        <p className="text-xs text-gray-500 text-center mt-2">
          {!original ? "Add the original audio" : !reference ? "Add your voice sample" : !consent ? "Tick the confirmation above" : ""}
        </p>
      )}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex items-center gap-3">
            <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
            <div>
              <p className="font-semibold">Converting the voice... {formatSeconds(elapsed)}</p>
              <p className="text-sm text-gray-400">This usually takes 20 to 90 seconds, longer for long recordings or when the free AI service is busy. You can stay on this page.</p>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 mt-4 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-brand-gradient animate-pulse" />
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The conversion failed. Please try again."}</span>
        </div>
      )}

      {latest && latest.status === "completed" && latest.result_audio && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Your result</p>
          <audio controls src={latest.result_audio} className="w-full mb-4" />
          <a href={latest.result_audio} download className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-white text-sm font-medium transition-colors">
            <FiDownload /> Download audio
          </a>
          <p className="text-xs text-gray-500 mt-3">AI-generated voice. If it does not sound close enough, try a longer, cleaner voice sample or the Best quality.</p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your conversions</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing converted yet. Your results will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {history.map((job) => (
              <div key={job.id} className="rounded-xl bg-white/5 border border-navy-border p-4">
                <p className="text-sm text-gray-200 truncate">{job.original_name || "Recording"}</p>
                <p className="text-xs text-gray-500 mb-2">
                  {new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? "Ready" : job.status === "failed" ? "Failed" : "Converting..."}
                </p>
                {job.status === "completed" && job.result_audio && (
                  <div className="flex items-center gap-3">
                    <audio controls src={job.result_audio} className="w-full h-8" />
                    <a href={job.result_audio} download title="Download" className="text-gray-400 hover:text-accent-light shrink-0"><FiDownload size={16} /></a>
                    <button onClick={() => remove(job)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  </div>
                )}
                {job.status === "failed" && (
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs text-red-300">{job.error_message}</p>
                    <button onClick={() => remove(job)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
